import React, { useState } from 'react';

import { showToast } from '@opendoor/bricks/complex/Toast';
import { Button, DropdownSingleSelect, Flex } from '@opendoor/bricks/core';

import { useReassignLead } from '../../graphql/athena/mutations/ReassignLead';
import { useGetBrokerageAgents } from '../../graphql/athena/queries/GetBrokerageAgents';
import { useGetBrokerageId } from '../../graphql/athena/queries/GetBrokerageId';

type ReassignLeadFormProps = {
  agentCustomerUuid: string;
  agentLeadId: string;
};

interface BrokerageAgent {
  id: string;
  agentCustomerId: string;
  agentCustomer: {
    fullName: string;
  };
}

/** Drop this form in wherever we need to allow a 3rd party agent to reassign */
export const ReassignLeadForm: React.FC<ReassignLeadFormProps> = ({
  agentCustomerUuid,
  agentLeadId,
}) => {
  const [showForm, setShowForm] = useState(false);
  const [selectedOption, setSelectedOption] = useState('');

  const { reassignLead, loading, error } = useReassignLead();
  const { brokerageId: assignedBrokerageId } = useGetBrokerageId(agentCustomerUuid);
  const {
    agents,
    loading: brokerageAgentsLoading,
    error: brokerageAgentsError,
  } = useGetBrokerageAgents(assignedBrokerageId);
  const handleReassignAgent = async (agent: string) => {
    reassignLead(agentLeadId, agent)
      .then(() => {
        showToast('info', `Successfully transferred lead`, 'lwc-dashboard-toast}', {
          toastOptions: { autoClose: 3000 },
        });
      })
      .catch((_) => {
        // Just catch for now, log later
      });
  };

  const handleOptionChange = (option: string) => {
    setSelectedOption(option);
  };
  if (error || brokerageAgentsError) {
    showToast('error', 'Error transferring the lead', 'lwc-dashboard-toast', {
      toastOptions: { autoClose: false },
    });
  }

  /** Filter to exclude the current agent */
  const agentOptions =
    agents &&
    agents.map((agent: BrokerageAgent) => ({
      label: agent?.agentCustomer?.fullName || '',
      value: agent?.id || '',
    }));

  return (
    <Flex flexDirection="column">
      {showForm ? (
        <>
          <DropdownSingleSelect
            aria-labelledby=""
            autoComplete="off"
            title="Which agent would you like to assign the lead to?"
            placeholder="Select an option"
            selectedValue={selectedOption ? [selectedOption] : []}
            onChange={(option) => {
              if (typeof option === 'string') {
                handleOptionChange(option);
              }
            }}
            options={agentOptions || []}
          />
          <Button
            my={3}
            aria-label="Submit which agent should be assigned to this lead"
            analyticsName="set-agent-assignment"
            onClick={() => {
              handleReassignAgent(selectedOption);
            }}
            disabled={!selectedOption || loading || brokerageAgentsLoading}
          >
            Assign to agent
          </Button>
        </>
      ) : (
        <Button
          aria-label="Show the assignment form"
          analyticsName="show-assignment-form"
          onClick={async () => {
            setShowForm(true);
          }}
          variant="secondary"
        >
          Assign to agent
        </Button>
      )}
    </Flex>
  );
};
